import React, { useState, useEffect } from 'react';
import './Login.scss';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../config/configApi';
import { toast } from 'react-hot-toast';

import pattern from '../../assets/images/pattern.webp';
import { isEmpty } from 'lodash';
import * as motion from 'motion/react-client'

import Loading from '../../components/Loading';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { InputAdornment } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MuiCard from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import { MdEmail, MdVisibility } from 'react-icons/md';

const Card = styled(MuiCard)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'center',
    width: '100%',
    padding: theme.spacing(4),
    gap: theme.spacing(2),
    margin: 'auto',
    boxShadow:
        'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
    [theme.breakpoints.up('sm')]: {
        width: '450px',
    },
    ...theme.applyStyles('dark', {
        boxShadow:
            'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
    }),
}));

const SignUpContainer = styled(Stack)(({ theme }) => ({
    height: 'calc((1 - var(--template-frame-height, 0)) * 100dvh)',
    minHeight: '100%',
    padding: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(4),
    },
    '&::before': {
        content: '""',
        display: 'block',
        position: 'absolute',
        opacity: .1,
        zIndex: -1,
        inset: 0,
        backgroundImage: `url(${pattern})`,
    },
}));

const GreenButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.green,
  color: '#fff', // Garante um bom contraste, ou use uma cor específica
  '&:hover': {
    backgroundColor: `${theme.palette.primary.green}D0`, // Exemplo de hover mais escuro
  },
}));

function Login() {
    const { user, loadingAuth, login } = useAuth();    
    const [loading, setLoading] = useState(false);
    const [emailFocused, setEmailFocused] = useState(false);
    const [passwordFocused, setPasswordFocused] = useState(false);    
    const [showPassword, setShowPassword] = useState(false);

    const navigate = useNavigate();

    const [inputs, setInputs] = useState({
        email: '',
        password: ''
    })
    const [error, setError] = useState({
        email: null,
        password: null,
        login: null,
    });

    useEffect(() => {
        if (!loadingAuth && user) {
           setTimeout(() => navigate('/dashboard'), 4000)
        }
    }, [user, loadingAuth, navigate]);

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword)
    };

    const handleChange = (e) => {
        setInputs((prev) => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    }

    const handleSubmit = async (event) => {
        event.preventDefault();

        let formIsValid = true;

        if (isEmpty(inputs.email) || !/\S+@\S+\.\S+/.test(inputs.email)) {
            setError((prev) => ({ ...prev, email: 'Preencha com um email valido' }))
            formIsValid = false;
        } else {
            setError((prev) => ({ ...prev, email: null }));
        }

        if (isEmpty(inputs.password)) {
            setError((prev) => ({ ...prev, password: 'Preencha a senha' }))
            formIsValid = false;
        } else {
            setError((prev) => ({ ...prev, password: null }));
        }

        if (!formIsValid) return;

        setLoading(true);

        try {
            const res = await api.post('/api/auth/login', { inputs });               
            if (res.data.auth) {
                login(res.data.user);
                toast.success('Usuário logado');
                setError({ email: null, password: null, login: null });
            }
        } catch (err) {
            setError((prev) => ({ ...prev, login: err.response.data.msg }));
        } finally {
            setTimeout(() => setLoading(false), 3000);
        }
    };

    return (
        <>
            <div>
                {loadingAuth ? (
                    <Loading />
                ) : (
                    <>
                        <CssBaseline enableColorScheme />
                        <SignUpContainer
                            direction='column'
                            justifyContent='space-between'
                            sx={{ position: 'relative', zIndex: 98 }}
                        >
                            <motion.div
                                initial={{ opacity: 0, scale: 0.5 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{
                                    duration: 0.8,
                                    delay: 0.8,
                                    ease: [0, 0.71, 0.2, 1.01],
                                }}
                                style={{ display: 'flex', minHeight: '100%' }}
                            >
                                <Card variant='outlined'>
                                    <Typography
                                        component='h1'
                                        variant='h4'
                                        sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)', fontWeight: 500 }}
                                    >
                                        Entrar
                                    </Typography>
                                    <div id='form_title' />
                                    <Box
                                        component='form'
                                        onSubmit={handleSubmit}
                                        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                                    >
                                        <FormControl>
                                            <FormLabel
                                                htmlFor='email'
                                                sx={{ fontSize: '12px', fontWeight: 500, color: emailFocused ? 'primary.main' : '' }}
                                                error={error?.email}
                                            >
                                                Email
                                            </FormLabel>
                                            <TextField
                                                required
                                                fullWidth
                                                id='email'
                                                placeholder='exemplo@email.com'
                                                name='email'
                                                type='email'
                                                autoComplete='email'
                                                variant='outlined'
                                                error={error?.email}
                                                helperText={error?.email}
                                                color={error?.email ? 'error' : 'primary'}
                                                onChange={handleChange}
                                                onFocus={() => setEmailFocused(!emailFocused)}
                                                onBlur={() => setEmailFocused(!emailFocused)}
                                                slotProps={{
                                                    input: {
                                                        endAdornment: (
                                                            <InputAdornment position='end'>
                                                                <MdEmail />
                                                            </InputAdornment>
                                                        ),
                                                    },
                                                }}
                                            />
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel
                                                htmlFor='password'
                                                sx={{ fontSize: '12px', fontWeight: 500, color: passwordFocused ? 'primary.main' : '' }}
                                                error={error?.password}
                                            >
                                                Senha
                                            </FormLabel>
                                            <TextField
                                                required
                                                fullWidth
                                                name='password'
                                                placeholder='••••••'
                                                type={showPassword ? 'text' : 'password'}
                                                id='password'
                                                autoComplete='new-password'
                                                variant='outlined'
                                                error={error?.password}
                                                helperText={error?.password}
                                                color={error?.password ? 'error' : 'primary'}
                                                onChange={handleChange}
                                                onFocus={() => setPasswordFocused(!passwordFocused)}
                                                onBlur={() => setPasswordFocused(!passwordFocused)}
                                                slotProps={{
                                                    input: {
                                                        endAdornment: (
                                                            <InputAdornment position='end'>
                                                                <MdVisibility onClick={handleClickShowPassword} style={{ cursor: 'pointer' }} />
                                                            </InputAdornment>
                                                        ),
                                                    },
                                                }}
                                            />
                                        </FormControl>

                                        <div>
                                            {error?.login && (
                                                <Typography sx={{ color: 'red', fontSize: '12px' }}>{error?.login}</Typography>
                                            )}
                                        </div>

                                        <GreenButton
                                            type='submit'
                                            fullWidth
                                            variant='contained'                                            
                                            loading={loading}
                                            onClick={handleSubmit}
                                        >
                                            Login
                                        </GreenButton>
                                    </Box>
                                </Card>
                            </motion.div>                            
                        </SignUpContainer>
                    </>
                )}
            </div>
        </>
    )
}

export default Login;
